// Per-contact detail data for the modals that open from each /contacts demo
// card button: AI Conversation (thread + pending drafts), AI Notes (handover
// notes), Activity (timeline) and Notifications (saved preferences).
//
// While the /contacts sample view is on screen, useDemoNetworkGuard keeps
// window.__demoMode on, so every modal's backend fetch is short-circuited to an
// empty response. Each modal instead resolves its sample data from the getters
// below, keyed by the same contact identity the row was built from. Shapes
// mirror the real endpoint responses so the modal UIs render unchanged:
//
//   getDemoThread(contactKey)      -> /ai-conversations/thread
//   getDemoHandoverNotes(identity) -> /agent-list-contact-handover-notes
//   getDemoActivity(email)         -> /activity-explorer
//   getDemoContactSettings(email)  -> /agent-read-contact-settings
//
// The data is self-consistent with demoContacts.js (names, scores, ownership,
// notes_count, tags), demoConversations.js (draft copy) and
// demoPropertySearches.js (addresses, valuations) so the whole demo reads like
// one coherent book of leads rather than disconnected samples.

const NOW = Date.now();
const iso = (mins) => new Date(NOW - mins * 60000).toISOString();
const mins = (m) => m;
const hours = (h) => h * 60;
const days = (d) => d * 24 * 60;

const AI_AGENT_NAME = "Olivia Bennett";
const AI_AGENT_EMAIL = "olivia@exampleagency.com";

const tag = (slug, label, color, source = "ai", category = "status") => ({
  slug, label, color, category, source,
});

// Tag definitions reused from demoContacts.js so the modal header chips match
// the row chips exactly.
const TAGS = {
  hotLead: tag("hot-lead", "Hot lead", "#ef4444"),
  seller: tag("seller", "Seller", "#0d9488", "human", "intent"),
  nurturing: tag("nurturing", "Nurturing", "#f59e0b"),
  buyer: tag("buyer", "Buyer", "#3b82f6", "human", "intent"),
  pastClient: tag("past-client", "Past client", "#8b5cf6", "human", "relationship"),
};

// ── AI Conversation threads ─────────────────────────────────────────────
// Shape mirrors the /ai-conversations/thread success payload consumed by
// ThreadView: { status, ai_owns_relationship, ai_agent_*, contact, messages,
// pending_drafts }. Messages are oldest-first (ThreadView renders in order).
const DEMO_THREADS = {
  "e:sarah.mitchell@example.com": {
    status: "success",
    ai_owns_relationship: true,
    ai_agent_name: AI_AGENT_NAME,
    ai_agent_email: AI_AGENT_EMAIL,
    contact: {
      first_name: "Sarah", last_name: "Mitchell",
      email: "sarah.mitchell@example.com",
      clean_mobile: "0412 345 678", mobile: "0412 345 678",
      suburb: "Balmain", state: "NSW", postcode: "2041",
      handover_notes_count: 2,
      tags: [TAGS.hotLead, TAGS.seller],
      channels: { email: { available: true, reachable: true } },
    },
    pending_drafts: [
      {
        id: "demo-draft-sarah",
        kind: "reply",
        channel: "email",
        contact_key: "e:sarah.mitchell@example.com",
        contact_email: "sarah.mitchell@example.com",
        contact_phone: "0412 345 678",
        ai_owns_relationship: true,
        draft_version: 1,
        created_at: iso(mins(8)),
        ai_agent_name: AI_AGENT_NAME,
        ai_agent_email: AI_AGENT_EMAIL,
        trigger_names: ["Replied to your appraisal email"],
        email_subject: "Re: Your property appraisal for 14 Darling Street",
        email_body:
          "Hi Sarah,\n\nGreat to hear you're considering listing — late autumn into winter is still seeing strong buyer demand in Balmain, especially for renovated terraces like yours.\n\nI'd love to talk through timing, recent comparable sales, and how I market homes to get the best result. Are you free for a quick call tomorrow afternoon, or would Saturday morning suit better?\n\nWarm regards,\nOlivia Bennett\nAssociate to John Doe",
        body:
          "Hi Sarah,\n\nGreat to hear you're considering listing — late autumn into winter is still seeing strong buyer demand in Balmain, especially for renovated terraces like yours.\n\nI'd love to talk through timing, recent comparable sales, and how I market homes to get the best result. Are you free for a quick call tomorrow afternoon, or would Saturday morning suit better?\n\nWarm regards,\nOlivia Bennett\nAssociate to John Doe",
      },
    ],
    messages: [
      {
        message_id: "demo-msg-sarah-1",
        direction: "outbound",
        channel: "email",
        from_email: AI_AGENT_EMAIL,
        to_email: "sarah.mitchell@example.com",
        subject: "Your property appraisal for 14 Darling Street",
        message_text:
          "Hi Sarah,\n\nThanks for requesting an appraisal on 14 Darling Street. Based on recent sales of comparable renovated terraces in Balmain, I've estimated your home at around $1.75M–$1.95M.\n\nI've attached the full report. Happy to walk you through how I arrived at the figure whenever suits.\n\nWarm regards,\nOlivia Bennett\nAssociate to John Doe",
        created_at: iso(days(5)),
      },
      {
        message_id: "demo-msg-sarah-2",
        direction: "inbound",
        channel: "email",
        from_email: "sarah.mitchell@example.com",
        to_email: AI_AGENT_EMAIL,
        subject: "Re: Your property appraisal for 14 Darling Street",
        message_text:
          "Hi, thanks for the appraisal on Darling Street. We're thinking of listing in the next month or two — could you give me a call to talk through timing and your commission?",
        created_at: iso(mins(35)),
      },
    ],
  },

  "e:james.nguyen@example.com": {
    status: "success",
    ai_owns_relationship: true,
    ai_agent_name: AI_AGENT_NAME,
    ai_agent_email: AI_AGENT_EMAIL,
    contact: {
      first_name: "James", last_name: "Nguyen",
      email: "james.nguyen@example.com",
      clean_mobile: "0423 887 210", mobile: "0423 887 210",
      suburb: "Hawthorn", state: "VIC", postcode: "3122",
      handover_notes_count: 0,
      tags: [TAGS.nurturing],
      channels: { email: { available: true, reachable: true } },
    },
    pending_drafts: [
      {
        id: "demo-draft-james",
        kind: "outbound",
        channel: "email",
        contact_key: "e:james.nguyen@example.com",
        contact_email: "james.nguyen@example.com",
        contact_phone: "0423 887 210",
        ai_owns_relationship: true,
        draft_version: 1,
        created_at: iso(mins(40)),
        ai_agent_name: AI_AGENT_NAME,
        ai_agent_email: AI_AGENT_EMAIL,
        trigger_names: ["Viewed a property report 3 times this week"],
        email_subject: "A few homes you might like in Hawthorn",
        email_body:
          "Hi James,\n\nI noticed you've been keeping an eye on properties around Glenferrie Road. The market there has picked up over the last few weeks, and a couple of townhouses similar to the ones you viewed have just come up.\n\nWould you like me to send through a shortlist and the latest price guides? Happy to keep you posted as new listings hit the market.\n\nCheers,\nOlivia Bennett\nAssociate to John Doe",
        body:
          "Hi James,\n\nI noticed you've been keeping an eye on properties around Glenferrie Road. The market there has picked up over the last few weeks, and a couple of townhouses similar to the ones you viewed have just come up.\n\nWould you like me to send through a shortlist and the latest price guides? Happy to keep you posted as new listings hit the market.\n\nCheers,\nOlivia Bennett\nAssociate to John Doe",
      },
    ],
    messages: [
      {
        message_id: "demo-msg-james-1",
        direction: "outbound",
        channel: "email",
        from_email: AI_AGENT_EMAIL,
        to_email: "james.nguyen@example.com",
        subject: "Your Hawthorn market update",
        message_text:
          "Hi James,\n\nWelcome aboard! I'll keep you posted on what's happening around Hawthorn — price movements, new listings and recent sales.\n\nIf there's a particular street or property type you're focused on, just let me know and I'll tailor what I send.\n\nCheers,\nOlivia Bennett\nAssociate to John Doe",
        created_at: iso(days(12)),
      },
      {
        message_id: "demo-msg-james-2",
        direction: "outbound",
        channel: "email",
        from_email: AI_AGENT_EMAIL,
        to_email: "james.nguyen@example.com",
        subject: "That Glenferrie Road townhouse you looked at",
        message_text:
          "Hi James,\n\nI saw you came back to the report on the Glenferrie Road townhouse a couple of times — it's a strong example of what's selling well right now.\n\nWant me to set up an alert so you're first to know when similar ones list?\n\nCheers,\nOlivia Bennett\nAssociate to John Doe",
        created_at: iso(days(4)),
      },
    ],
  },

  "e:priya.sharma@example.com": {
    status: "success",
    ai_owns_relationship: false,
    ai_agent_name: AI_AGENT_NAME,
    ai_agent_email: AI_AGENT_EMAIL,
    contact: {
      first_name: "Priya", last_name: "Sharma",
      email: "priya.sharma@example.com",
      clean_mobile: "0438 552 901", mobile: "0438 552 901",
      suburb: "Toowong", state: "QLD", postcode: "4066",
      handover_notes_count: 1,
      tags: [TAGS.buyer],
      channels: { email: { available: true, reachable: true } },
    },
    pending_drafts: [],
    messages: [
      {
        message_id: "demo-msg-priya-1",
        direction: "outbound",
        channel: "sms",
        from_email: AI_AGENT_EMAIL,
        to_email: "0438 552 901",
        message_text:
          "Hi Priya, it's the team at your agency — a 2-bed unit in Toowong just listed close to your budget. Want me to send the link and arrange an inspection?",
        created_at: iso(days(8)),
      },
      {
        message_id: "demo-msg-priya-2",
        direction: "inbound",
        channel: "sms",
        from_email: "0438 552 901",
        to_email: AI_AGENT_EMAIL,
        message_text:
          "Yes please! Saturday inspections work best for me.",
        created_at: iso(days(7) + hours(2)),
      },
      {
        message_id: "demo-msg-priya-3",
        direction: "outbound",
        channel: "email",
        from_email: AI_AGENT_EMAIL,
        to_email: "priya.sharma@example.com",
        subject: "22 Sherwood Road, Toowong — inspection details",
        message_text:
          "Hi Priya,\n\nHere are the details for the Toowong unit, plus two others nearby in your price range. Saturday inspections are 10:00–10:30am.\n\nLet me know which you'd like to see and I'll confirm with the agents.\n\nKind regards,\nOlivia Bennett\nAssociate to John Doe",
        created_at: iso(days(6)),
      },
    ],
  },

  "e:tom.andersson@example.com": {
    status: "success",
    ai_owns_relationship: true,
    ai_agent_name: AI_AGENT_NAME,
    ai_agent_email: AI_AGENT_EMAIL,
    contact: {
      first_name: "Tom", last_name: "Andersson",
      email: "tom.andersson@example.com",
      clean_mobile: "0401 229 384", mobile: "0401 229 384",
      suburb: "Glenelg", state: "SA", postcode: "5045",
      handover_notes_count: 0,
      tags: [],
      channels: { email: { available: true, reachable: true } },
    },
    pending_drafts: [],
    messages: [
      {
        message_id: "demo-msg-tom-1",
        direction: "outbound",
        channel: "email",
        from_email: AI_AGENT_EMAIL,
        to_email: "tom.andersson@example.com",
        subject: "Your appraisal for 5 Moseley Street, Glenelg",
        message_text:
          "Hi Tom,\n\nThanks for requesting an appraisal on 5 Moseley Street. With its land size and recent beachside sales, I've put the estimate at $2.2M–$2.5M.\n\nThe full report is attached. No rush at all — I'm here whenever you'd like to talk it through.\n\nWarm regards,\nOlivia Bennett\nAssociate to John Doe",
        created_at: iso(days(9)),
      },
    ],
  },

  "e:olivia.brown@example.com": {
    status: "success",
    ai_owns_relationship: false,
    ai_agent_name: AI_AGENT_NAME,
    ai_agent_email: AI_AGENT_EMAIL,
    contact: {
      first_name: "Olivia", last_name: "Brown",
      email: "olivia.brown@example.com",
      clean_mobile: "0455 671 023", mobile: "0455 671 023",
      suburb: "Subiaco", state: "WA", postcode: "6008",
      handover_notes_count: 0,
      tags: [TAGS.pastClient],
      channels: { email: { available: true, reachable: true } },
    },
    pending_drafts: [],
    messages: [
      {
        message_id: "demo-msg-olivia-1",
        direction: "outbound",
        channel: "email",
        from_email: AI_AGENT_EMAIL,
        to_email: "olivia.brown@example.com",
        subject: "Your Subiaco market update",
        message_text:
          "Hi Olivia,\n\nHope you're settling in well! Here's the latest on Subiaco — median values are holding firm and stock is tight heading into spring.\n\nAny time you'd like an updated estimate on your place, just say the word.\n\nWarm regards,\nOlivia Bennett\nAssociate to John Doe",
        created_at: iso(days(14)),
      },
      {
        message_id: "demo-msg-olivia-2",
        direction: "inbound",
        channel: "email",
        from_email: "olivia.brown@example.com",
        to_email: AI_AGENT_EMAIL,
        subject: "Re: Your Subiaco market update",
        message_text:
          "Thanks for the market update — really helpful, I'll be in touch closer to spring.",
        created_at: iso(days(3)),
      },
    ],
  },

  "e:daniel.lee@example.com": {
    status: "success",
    ai_owns_relationship: true,
    ai_agent_name: AI_AGENT_NAME,
    ai_agent_email: AI_AGENT_EMAIL,
    contact: {
      first_name: "Daniel", last_name: "Lee",
      email: "daniel.lee@example.com",
      clean_mobile: "0467 318 552", mobile: "0467 318 552",
      suburb: "Newtown", state: "NSW", postcode: "2042",
      handover_notes_count: 0,
      tags: [],
      channels: { email: { available: true, reachable: true } },
    },
    pending_drafts: [],
    messages: [
      {
        message_id: "demo-msg-daniel-1",
        direction: "outbound",
        channel: "email",
        from_email: AI_AGENT_EMAIL,
        to_email: "daniel.lee@example.com",
        subject: "Your estimate for 97 King Street, Newtown",
        message_text:
          "Hi Daniel,\n\nHere's the estimate you requested for 97 King Street — currently sitting around $1.3M based on recent Newtown sales.\n\nLet me know if you'd like the detailed breakdown.\n\nCheers,\nOlivia Bennett\nAssociate to John Doe",
        created_at: iso(days(34)),
      },
      {
        message_id: "demo-msg-daniel-2",
        direction: "outbound",
        channel: "email",
        from_email: AI_AGENT_EMAIL,
        to_email: "daniel.lee@example.com",
        subject: "Checking in on King Street",
        message_text:
          "Hi Daniel,\n\nJust checking in — let me know if you'd like an updated estimate on King Street. The Newtown market has shifted a little since we last spoke.\n\nCheers,\nOlivia Bennett\nAssociate to John Doe",
        created_at: iso(days(3)),
      },
    ],
  },
};

// ── AI Handover Notes ───────────────────────────────────────────────────
// Shape mirrors /agent-list-contact-handover-notes -> { notes: [...] } where
// each note is { id, note, created_by, created_at }. Counts line up with the
// handover_notes_count on each demoContacts.js row (Sarah: 2, Priya: 1).
const DEMO_HANDOVER_NOTES = {
  "sarah.mitchell@example.com": [
    {
      id: "demo-note-sarah-1",
      created_by: "AI agent",
      created_at: iso(days(5)),
      note:
        "Requested a market appraisal for 14 Darling Street via the website. Renovated 4-bed terrace — strong comparable sales in Balmain this quarter. Estimated $1.75M–$1.95M.",
    },
    {
      id: "demo-note-sarah-2",
      created_by: "AI agent",
      created_at: iso(mins(30)),
      note:
        "Replied wanting to list in the next 1–2 months and asked about commission. Flagged as a hot lead — worth booking a listing presentation this week.",
    },
  ],
  "priya.sharma@example.com": [
    {
      id: "demo-note-priya-1",
      created_by: "You",
      created_at: iso(days(6)),
      note:
        "Buyer searching in Toowong, budget around $900k for a 2-bed unit. Prefers SMS and Saturday inspections. I'll handle her comms directly from here.",
    },
  ],
};

// ── Activity timeline ───────────────────────────────────────────────────
// Shape mirrors /activity-explorer -> { data: [emailRows], platform_events:
// [platformRows], total, has_more }. Email rows use EMAIL_EVENT_CONFIG event
// types + a campaign `tag`; platform rows use PLATFORM_EVENT_CONFIG types with
// a `detail` / `address_text`. message_id is intentionally omitted so the
// "View email →" deep link (which needs the live backend) doesn't render.
const emailEvt = (event_type, email, subject, tag, minsAgo, extra = {}) => ({
  event_type, email, subject, tag, created_at: iso(minsAgo), ...extra,
});
const platEvt = (event_type, email, minsAgo, extra = {}) => ({
  source_table: "events", event_type, email, created_at: iso(minsAgo),
  page_url: "https://homeprice.site", ...extra,
});

const DEMO_ACTIVITY = {
  "sarah.mitchell@example.com": {
    data: [
      emailEvt("Delivery", "sarah.mitchell@example.com", "Your property appraisal for 14 Darling Street", "appraisal_send", days(5) + mins(2)),
      emailEvt("Open", "sarah.mitchell@example.com", "Your property appraisal for 14 Darling Street", "appraisal_send", days(5) - hours(1)),
      emailEvt("Click", "sarah.mitchell@example.com", "Your property appraisal for 14 Darling Street", "appraisal_send", days(5) - hours(1) - mins(2), { clicked_url: "https://homeprice.site", country: "Australia" }),
      emailEvt("Open", "sarah.mitchell@example.com", "April market update — Balmain", "market_update", days(2)),
    ],
    platform_events: [
      platEvt("send_property_report", "sarah.mitchell@example.com", days(5) + mins(5), { detail: "Report sent for 14 Darling Street, Balmain NSW 2041", address_text: "14 Darling Street, Balmain NSW 2041" }),
      platEvt("view_appraisal", "sarah.mitchell@example.com", days(5) - hours(1) - mins(5), { detail: "Viewed appraisal for 14 Darling Street", address_text: "14 Darling Street, Balmain NSW 2041" }),
      platEvt("property_analysed", "sarah.mitchell@example.com", days(5) - hours(1) - mins(8), { detail: "Analysed 14 Darling Street, Balmain NSW 2041", address_text: "14 Darling Street, Balmain NSW 2041" }),
      platEvt("scroll_depth", "sarah.mitchell@example.com", days(5) - hours(1) - mins(10), { detail: "Scrolled 75% on appraisal page" }),
    ],
  },
  "james.nguyen@example.com": {
    data: [
      emailEvt("Delivery", "james.nguyen@example.com", "Your Hawthorn market update", "market_update", days(12) + mins(2)),
      emailEvt("Open", "james.nguyen@example.com", "Your Hawthorn market update", "market_update", days(12) - hours(3)),
      emailEvt("Delivery", "james.nguyen@example.com", "That Glenferrie Road townhouse you looked at", "property_update", days(4) + mins(1)),
      emailEvt("Open", "james.nguyen@example.com", "That Glenferrie Road townhouse you looked at", "property_update", days(4) - hours(2)),
      emailEvt("Click", "james.nguyen@example.com", "That Glenferrie Road townhouse you looked at", "property_update", days(4) - hours(2) - mins(1), { clicked_url: "https://homeprice.site", country: "Australia" }),
    ],
    platform_events: [
      platEvt("property_analysed", "james.nguyen@example.com", days(6), { detail: "Analysed 8 Glenferrie Road, Hawthorn VIC 3122", address_text: "8 Glenferrie Road, Hawthorn VIC 3122" }),
      platEvt("view_appraisal", "james.nguyen@example.com", days(5) - hours(4), { detail: "Viewed report for 8 Glenferrie Road", address_text: "8 Glenferrie Road, Hawthorn VIC 3122" }),
      platEvt("view_appraisal", "james.nguyen@example.com", days(3) - hours(2), { detail: "Viewed report for 8 Glenferrie Road (return visit)", address_text: "8 Glenferrie Road, Hawthorn VIC 3122" }),
      platEvt("click_track_property", "james.nguyen@example.com", days(3) - hours(1), { detail: "Added 8 Glenferrie Road to tracked properties", address_text: "8 Glenferrie Road, Hawthorn VIC 3122" }),
      platEvt("get_article", "james.nguyen@example.com", days(2), { detail: 'Read "Is now a good time to buy in Hawthorn?"' }),
    ],
  },
  "priya.sharma@example.com": {
    data: [
      emailEvt("Delivery", "priya.sharma@example.com", "22 Sherwood Road, Toowong — inspection details", "property_update", days(6) + mins(1)),
      emailEvt("Open", "priya.sharma@example.com", "22 Sherwood Road, Toowong — inspection details", "property_update", days(6) - hours(5)),
    ],
    platform_events: [
      platEvt("sms_sent", "0438 552 901", days(8), { detail: "SMS sent about Toowong unit" }),
      platEvt("property_analysed", "priya.sharma@example.com", days(7), { detail: "Analysed 22 Sherwood Road, Toowong QLD 4066", address_text: "22 Sherwood Road, Toowong QLD 4066" }),
      platEvt("view_appraisal", "priya.sharma@example.com", days(6) - hours(4), { detail: "Viewed estimate for 22 Sherwood Road", address_text: "22 Sherwood Road, Toowong QLD 4066" }),
    ],
  },
  "tom.andersson@example.com": {
    data: [
      emailEvt("Delivery", "tom.andersson@example.com", "Your appraisal for 5 Moseley Street, Glenelg", "appraisal_send", days(9) + mins(2)),
      emailEvt("Open", "tom.andersson@example.com", "Your appraisal for 5 Moseley Street, Glenelg", "appraisal_send", days(9) - hours(6)),
    ],
    platform_events: [
      platEvt("send_property_report", "tom.andersson@example.com", days(9) + mins(5), { detail: "Report sent for 5 Moseley Street, Glenelg SA 5045", address_text: "5 Moseley Street, Glenelg SA 5045" }),
      platEvt("view_appraisal", "tom.andersson@example.com", days(9) - hours(6) - mins(2), { detail: "Viewed appraisal for 5 Moseley Street", address_text: "5 Moseley Street, Glenelg SA 5045" }),
    ],
  },
  "olivia.brown@example.com": {
    data: [
      emailEvt("Delivery", "olivia.brown@example.com", "Your Subiaco market update", "market_update", days(14) + mins(1)),
      emailEvt("Open", "olivia.brown@example.com", "Your Subiaco market update", "market_update", days(13)),
    ],
    platform_events: [],
  },
  "daniel.lee@example.com": {
    data: [
      emailEvt("Delivery", "daniel.lee@example.com", "Your estimate for 97 King Street, Newtown", "property_report", days(34) + mins(1)),
      emailEvt("Open", "daniel.lee@example.com", "Your estimate for 97 King Street, Newtown", "property_report", days(33)),
      emailEvt("Delivery", "daniel.lee@example.com", "Checking in on King Street", "property_update", days(3) + mins(1)),
    ],
    platform_events: [],
  },
};

// ── Notification settings ───────────────────────────────────────────────
// Shape mirrors /agent-read-contact-settings -> { property_updates: { enabled,
// frequency, selected_properties }, market_updates: { enabled, frequency,
// selected_suburbs } }. Enabled flags line up with the notif() values on each
// demoContacts.js row.
const prop = (label, value) => ({ label, value });
const DEMO_CONTACT_SETTINGS = {
  "sarah.mitchell@example.com": {
    property_updates: { enabled: true, frequency: "monthly", selected_properties: [prop("14 Darling Street, Balmain NSW 2041", "14-darling-st-balmain")] },
    market_updates: { enabled: true, frequency: "monthly", selected_suburbs: [prop("Balmain NSW 2041", "Balmain")] },
  },
  "james.nguyen@example.com": {
    property_updates: { enabled: true, frequency: "fortnightly", selected_properties: [prop("8 Glenferrie Road, Hawthorn VIC 3122", "8-glenferrie-rd-hawthorn")] },
    market_updates: { enabled: false, frequency: "monthly", selected_suburbs: [] },
  },
  "priya.sharma@example.com": {
    property_updates: { enabled: false, frequency: "monthly", selected_properties: [] },
    market_updates: { enabled: true, frequency: "weekly", selected_suburbs: [prop("Toowong QLD 4066", "Toowong")] },
  },
  "tom.andersson@example.com": {
    property_updates: { enabled: false, frequency: "monthly", selected_properties: [] },
    market_updates: { enabled: false, frequency: "monthly", selected_suburbs: [] },
  },
  "olivia.brown@example.com": {
    property_updates: { enabled: true, frequency: "quarterly", selected_properties: [prop("31 Rokeby Road, Subiaco WA 6008", "31-rokeby-rd-subiaco")] },
    market_updates: { enabled: true, frequency: "monthly", selected_suburbs: [prop("Subiaco WA 6008", "Subiaco")] },
  },
  "daniel.lee@example.com": {
    property_updates: { enabled: false, frequency: "monthly", selected_properties: [] },
    market_updates: { enabled: false, frequency: "monthly", selected_suburbs: [] },
  },
};

// ── Getters used by the modals ──────────────────────────────────────────
const normEmail = (e) => (e || "").trim().toLowerCase();

// contact_keys with a pending AI draft — used by /contacts to light up the
// "needs review" badge on the AI Conversation button while in demo mode
// (the real source, /ai-outbound-queue/list, is blocked by the demo guard).
export const DEMO_PENDING_REVIEW_KEYS = Object.entries(DEMO_THREADS)
  .filter(([, t]) => Array.isArray(t.pending_drafts) && t.pending_drafts.length > 0)
  .map(([key]) => key);

export function getDemoThread(contactKey) {
  if (!contactKey) return null;
  return DEMO_THREADS[contactKey] || null;
}

export function getDemoHandoverNotes(identity) {
  const email = normEmail(identity?.contact_email);
  return email ? (DEMO_HANDOVER_NOTES[email] || []) : [];
}

export function getDemoActivity(email) {
  const e = normEmail(email);
  return DEMO_ACTIVITY[e] || { data: [], platform_events: [] };
}

export function getDemoContactSettings(email) {
  const e = normEmail(email);
  return DEMO_CONTACT_SETTINGS[e] || null;
}

export { DEMO_THREADS, DEMO_HANDOVER_NOTES, DEMO_ACTIVITY, DEMO_CONTACT_SETTINGS };
